// ─────────────────────────────────────────────────────────────────────────────
// One resident's restraint worksheet — the screen a nurse actually works from.
//
// The loop this exists for: something happened (a risk finding, a fall, a restraint
// newly coded), the nurse opens the audit, the system has already filled in every
// row it could find evidence for, and the nurse works down the rest reading the
// chart — entering what they find, in their own words, on the rows software cannot
// reach. Then they sign it.
//
// ── THE THREE THINGS THIS SCREEN MUST NOT DO ────────────────────────────────
//
// 1. Hide that a row was answered by a PERSON rather than found in the data. Once
//    both can answer, "9 of 11 met" means two different things and a surveyor cares
//    which. Every answered row is labelled, and the header carries the split.
//
// 2. Throw away the system's finding when a nurse overrides it. A row where the
//    nurse wrote Y over a system N is the most interesting row on the sheet — it
//    means the evidence is real but not where we can read it. Both are shown, and
//    the disagreement is called out rather than resolved.
//
// 3. Let a signature cover rows the signer did not see. Sign-off needs a name and a
//    date, and a signed sheet is read-only afterwards — the next cycle is a new
//    review.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchResidentWorksheet,
  startReview,
  saveReview,
  fetchIncidents,
  pullFromChart,
  acceptSuggestion,
  dismissSuggestion,
} from "./postIncidentAuditApi";
import { loadSampleDraft, saveSampleDraft, clearSampleDraft } from "./sampleDrafts";
import SAMPLE from "../demo/restraintAuditSample.json";
import { postIncidentAspectLabel } from "./postIncidentAuditAccess";
import { buildAuditDocx, auditFileName, downloadBlob } from "./postIncidentAuditDocx";
import { rowMark, answeredBy, effectiveState, markOf, MARK, MARK_ORDER } from "./rowMark";

/**
 * The gap marker's pulse.
 *
 * A gap is the model saying "nothing in this chart supports this requirement" — the
 * thing a coordinator most needs to see, so it moves. It stops moving for anyone who
 * has asked their system not to animate: a compliance screen that ignores
 * prefers-reduced-motion is a screen somebody cannot use, and the colour and the
 * exclamation carry the meaning on their own.
 */
const GAP_PULSE_CSS = `
@keyframes pia-gap-pulse {
  0%, 100% { opacity: 1;    box-shadow: 0 0 0 0 rgba(180,83,9,.45); }
  50%      { opacity: .72;  box-shadow: 0 0 0 5px rgba(180,83,9,0); }
}
.pia-gap-mark { animation: pia-gap-pulse 1.6s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .pia-gap-mark { animation: none; } }

/* THE GRID HAS TO GIVE UP BEING A GRID BEFORE IT SCROLLS SIDEWAYS.
   Aligned columns are the whole value of the compact shape, and they need width for
   it. Below that width the row stacks — which is the old vertical layout, correctly,
   for a screen where nothing else fits. A worksheet that scrolls horizontally on a
   tablet is a worksheet with a hidden answer column. */
@media (max-width: 720px) {
  .pia-row > div:first-child {
    grid-template-columns: 22px 1fr auto !important;
    row-gap: 7px !important;
  }
  .pia-row > div:first-child > *:nth-child(2) { grid-column: 2 / -1; }
  .pia-row > div:first-child > *:nth-child(4) { grid-column: 2; }
  .pia-row > div:first-child > *:nth-child(5) { grid-column: 2 / -1; }
  /* The verdict mark is a glyph AND a word, so it will not survive being auto-placed
     into the 22px number column: it would wrap "Not met" to two lines or push the
     column open and take the alignment of every row above it with it. */
  .pia-row > div:first-child > *:nth-child(6) { grid-column: 2 / -1; text-align: left !important; }
}
/* The column header is the grid's promise that the columns mean something. It is
   meaningless once the row stacks, so it goes with them. */
@media (max-width: 720px) { .pia-colhead { display: none !important; } }
`;

/* One definition, used by the header and by every row — a header whose columns can
   drift from the rows' is worse than no header. */
// ── TYPE SCALE ───────────────────────────────────────────────────────────────
//
// THIS SHEET WAS TOO SMALL TO READ COMFORTABLY. Forty-nine of its fifty font sizes were
// 13px or under: the indicator question itself was 13px at line-height 1.35, the source
// line under it 11px, and the note field a nurse types into 12px.
//
// That is not a style preference. This is a clinical worksheet worked down row by row,
// often at a cart, often by someone who has been on shift for nine hours, and the text it
// compresses hardest is the question being answered. Dense is fine for a dashboard that
// gets scanned; it is the wrong default for a form that gets read.
//
// Sizes now come from here, so the whole sheet moves together and nobody has to find
// fifty call sites again. Three deliberate merges: 11 and 11.5 became one step, and so
// did 12.5 and 13 where they were doing the same job -- half-pixel distinctions nothing
// could perceive and everybody had to maintain.
//
// Print sizes are in points and are NOT touched: paper has different constraints and the
// print stylesheet was set against a real page.
const T = {
  micro: 11.5,  // was 10   — the smallest label on the sheet
  xs: 12.5,     // was 11 and 11.5 — provenance, timestamps, "entered by"
  sm: 13.5,     // was 12   — secondary prose, inputs
  base: 14,     // was 12.5 — supporting text under a heading
  md: 15,       // was 13   — THE INDICATOR QUESTION, and anything read like it
  lg: 15.5,     // was 13.5
  xl: 17,       // was 15.5 — the worksheet title
  display: 28,  // was 26   — the coverage percentage
};

// Line height, by what the text is for. 1.35 is a headline value and the sheet was using
// it on wrapped body copy.
const LH = { tight: 1.4, body: 1.55, prose: 1.65 };

// The last column was 44px, which fit a single letter. It now carries the row's verdict
// as a glyph and a word ("Not met", "Met — you"), because a column of letters is a column
// a reader has to decode and the whole ask was a sheet that can be scanned. 96px is the
// widest mark plus its glyph at T.xs; `auto` lets it take more on a wide screen rather
// than wrapping.
const PIA_GRID_COLUMNS = "22px minmax(180px, 1.7fr) 40px auto minmax(150px, 1fr) minmax(96px, auto)";

/**
 * The print sheet.
 *
 * A nurse finishing an audit needs it on paper — for the QAPI binder, and because a
 * surveyor asks for the document rather than a login. So this prints as the facility's
 * own form: the eleven rows, the Y/N/NA column, the header fields and the signature.
 *
 * TWO PARTS, NOT ONE. The grid alone is what their paper form is, and it is not enough:
 * the whole value of this tool is the REASON on each row — what the record showed, what
 * the reviewer found, and on an unanswered row why it could not be answered. A printout
 * of just the letters would hand a surveyor a sheet of Ns with nothing behind them. So
 * the grid comes first for the binder, and the detail follows for the defence.
 *
 * The cell states print as LETTERS and not as colour. A colour-coded compliance document
 * photocopied in black and white is a document where Y and N look the same.
 */
const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #pia-print, #pia-print * { visibility: visible !important; }
  #pia-print {
    position: absolute; left: 0; top: 0; width: 100%;
    font: 11pt/1.45 Georgia, "Times New Roman", serif; color: #000; background: #fff;
  }
  /* The screen worksheet is hidden while printing; the print sheet is hidden on screen. */
  #pia-screen { display: none !important; }
  .pia-print-grid { width: 100%; border-collapse: collapse; margin-top: 10pt; }
  .pia-print-grid th, .pia-print-grid td {
    border: 1px solid #000; padding: 4pt 6pt; text-align: left; vertical-align: top;
    font-size: 10pt;
  }
  .pia-print-grid th { background: #eee !important; -webkit-print-color-adjust: exact; }
  .pia-print-grid td.state { text-align: center; font-weight: bold; width: 44pt; }
  .pia-detail { page-break-before: always; }
  .pia-detail h3 { font-size: 12pt; margin: 14pt 0 4pt; }
  .pia-row-detail { margin: 0 0 9pt; padding-left: 16pt; }
  .pia-quote { font-style: italic; margin: 3pt 0 0 10pt; }
  .pia-sig { margin-top: 18pt; }
  .pia-sig div { margin-top: 12pt; border-bottom: 1px solid #000; width: 60%; }
  @page { margin: 14mm; }
}
#pia-print { display: none; }
@media print { #pia-print { display: block; } }
`;

const PALETTE = {
  Y: { bg: "#dcfce7", fg: "#14532d", border: "#86efac" },
  N: { bg: "#fee2e2", fg: "#7f1d1d", border: "#fca5a5" },
  NA: { bg: "var(--ed-line-2)", fg: "var(--ed-ink-3)", border: "var(--ed-line-hover)" },
  // NOT A FAILURE — a question for a person. The coolest cell on the sheet.
  "?": { bg: "var(--ed-brand-soft)", fg: "var(--ed-info-deep)", border: "var(--ed-info-line)" },
};

/** The Y / N / NA control. '?' is not offered: it is not a person's answer to give. */
const StatePicker = ({ value, onChange, disabled }) => (
  <div style={{ display: "inline-flex", gap: 4 }}>
    {["Y", "N", "NA"].map((s) => {
      const on = value === s;
      const p = PALETTE[s];
      return (
        <button
          key={s}
          type="button"
          disabled={disabled}
          onClick={() => onChange(on ? null : s)}
          title={on ? "Click again to clear this answer" : `Mark ${s}`}
          style={{
            minWidth: 34,
            padding: "3px 8px",
            borderRadius: 6,
            cursor: disabled ? "default" : "pointer",
            fontWeight: 800,
            fontSize: T.sm,
            background: on ? p.bg : "var(--ed-canvas)",
            color: on ? p.fg : "var(--ed-muted-2)",
            border: `1px solid ${on ? p.border : "#e2e8f0"}`,
            opacity: disabled ? 0.55 : 1,
          }}
        >
          {s}
        </button>
      );
    })}
  </div>
);

const Chip = ({ state, title }) => {
  const p = PALETTE[state] || PALETTE["?"];
  return (
    <span
      title={title}
      style={{
        display: "inline-block",
        minWidth: 28,
        textAlign: "center",
        padding: "1px 6px",
        borderRadius: 5,
        background: p.bg,
        color: p.fg,
        border: `1px solid ${p.border}`,
        fontWeight: 800,
        fontSize: T.xs,
      }}
    >
      {state}
    </span>
  );
};

/**
 * THE ROW'S VERDICT — the column a reader scans down to find their own work.
 *
 * Glyph, then word, then colour, in that order of load-bearing. See `rowMark.js` for why
 * there are five of these and why "Not read" is not allowed to resolve to either of its
 * neighbours.
 *
 * It is a BUTTON, not a chip, and the same button the record's own chip is: whatever the
 * row reads as, the next question is always why, and the reason is one click away on the
 * one thing the reader was already looking at. A row with no system finding at all has no
 * reason to show, so that case renders as plain text rather than a control that does
 * nothing when pressed.
 */
const RowMark = ({ entered, systemState, onToggle, expanded }) => {
  const m = rowMark({ entered, systemState });
  const inner = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 7px",
        borderRadius: 6,
        background: m.tone.bg,
        color: m.tone.fg,
        border: `1px solid ${m.tone.border}`,
        fontSize: T.xs,
        fontWeight: 700,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ fontWeight: 800 }}>{m.glyph}</span>
      {m.word}
    </span>
  );
  if (!onToggle) return <span title={m.title}>{inner}</span>;
  return (
    <button
      type="button"
      onClick={onToggle}
      title={`${m.title}${expanded ? "" : " — click for what the record shows"}`}
      aria-expanded={expanded}
      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", lineHeight: 1 }}
    >
      {inner}
    </button>
  );
};

/**
 * What the five marks mean, said once, where the columns are named.
 *
 * Not a tooltip: a legend a nurse reads on their first sheet and never again is cheaper
 * than five tooltips they have to hunt for, and this sheet gets handed to someone new
 * every time a facility turns over a DON.
 */
const MarkLegend = ({ marks = null }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "6px 14px",
      alignItems: "center",
      fontSize: T.xs,
      color: "var(--ed-muted)",
      lineHeight: LH.body,
    }}
  >
    {MARK_ORDER.map((k) => {
      const m = markOf(k);
      // A ZERO IS SHOWN, NOT HIDDEN. "0 not met" is a result; a missing row reads as a
      // bucket nobody counted. The counts are absent entirely only before the sheet has
      // loaded any rows, and then the legend is just a legend.
      const n = marks ? marks[k] || 0 : null;
      return (
        <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 17,
              height: 17,
              borderRadius: 5,
              background: m.tone.bg,
              color: m.tone.fg,
              border: `1px solid ${m.tone.border}`,
              fontWeight: 800,
              fontSize: 10.5,
            }}
          >
            {m.glyph}
          </span>
          {n == null ? null : (
            <strong style={{ color: m.tone.fg, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{n}</strong>
          )}
          <strong style={{ color: "var(--ed-ink-3)", fontWeight: 700 }}>{m.word}</strong>
          {m.mark === MARK.NOT_READ ? (
            <span>no answer either way — click the row for why, it counts in no percentage</span>
          ) : m.mark === MARK.MET_DATA ? (
            <span>answered from the chart</span>
          ) : m.mark === MARK.MET_PERSON ? (
            <span>a reviewer answered it</span>
          ) : m.mark === MARK.NOT_MET ? (
            <span>a gap</span>
          ) : (
            <span>does not apply</span>
          )}
        </span>
      );
    })}
  </div>
);

/** The percentage and its coverage, as ONE statement — they never separate. */
const Headline = ({ coverage, threshold }) => {
  const c = coverage || {};
  const pct = c.compliance == null ? null : Math.round(c.compliance * 1000) / 10;
  const met = c.compliance != null && c.compliance >= threshold;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: T.display,
            fontWeight: 800,
            color: pct == null ? "var(--ed-muted)" : met ? "#15803d" : "#b91c1c",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {pct == null ? "—" : `${pct}%`}
        </span>
        <span style={{ fontSize: T.md, color: "var(--ed-ink-3)" }}>
          {pct == null ? (
            <>nothing answered yet — no percentage can be computed</>
          ) : (
            <>
              of <strong>{c.verified}</strong> answered {c.verified === 1 ? "row" : "rows"} · threshold{" "}
              {Math.round(threshold * 100)}% applies to all <strong>{c.total}</strong>
            </>
          )}
        </span>
      </div>
      {/* THE SPLIT. "9 of 11 met" cannot say whether the facility verified those
          rows or a person attested to them, and that difference is the whole reason
          this worksheet has two authors. */}
      <div style={{ fontSize: T.base, color: "var(--ed-muted)", marginTop: 5 }}>
        <strong>{c.fromData || 0}</strong> verified from the record ·{" "}
        <strong>{c.fromPerson || 0}</strong> entered by a reviewer · <strong>{c.unknown || 0}</strong> still to check
        {c.notApplicable ? <> · {c.notApplicable} N/A</> : null}
        {c.disagreements ? (
          <span style={{ color: "var(--ed-amber)", fontWeight: 700 }}> · {c.disagreements} differ from the record</span>
        ) : null}
      </div>
    </div>
  );
};

/**
 * One thing the model proposed, beside the row it bears on.
 *
 * It is NOT a cell and never renders as one. An EVIDENCE card shows the verbatim quote
 * and offers to write it as YOUR answer; a GAP card shows what should be in the record
 * and offers nothing to accept, because a gap is not an answer — the evidence may sit
 * in the order list or the paper chart, and "accepting" it would mean asserting
 * non-compliance from our own blind spot.
 */
// ── WAS THIS WRITTEN BEFORE OR AFTER THE EVENT IT IS SUPPOSED TO BE ABOUT? ───
//
// A post-incident audit is an audit OF AN EVENT, and nothing compared the two. The note
// window is 120 days back from TODAY; each note carries its own date; the incident went
// in as a block. So a note written three weeks BEFORE a fall could be proposed as
// evidence that the care plan was revised in response to it — real quote, real note,
// right row, wrong in the one way a compliance document must not be.
//
// NOT A FILTER, because the opposite is also true. "Other methods tried PRIOR TO use of
// the restraint" wants pre-event evidence; "was the care plan revised" wants post. A
// rule that dropped one would make the other unanswerable. Saying WHEN lets a nurse
// apply the judgement the row actually calls for.
const DAY = 86400000;
function positionAgainstEvent(sourceDate, eventDate) {
  if (!sourceDate || !eventDate) return null;
  const a2 = Date.parse(`${String(sourceDate).slice(0, 10)}T00:00:00Z`);
  const b2 = Date.parse(`${String(eventDate).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(a2) || !Number.isFinite(b2)) return null;
  const days = Math.round((a2 - b2) / DAY);
  if (days === 0) return { days, side: "same", label: "same day as the event" };
  const n = Math.abs(days);
  const unit = n === 1 ? "day" : "days";
  return days > 0
    ? { days, side: "after", label: `${n} ${unit} after the event` }
    : { days, side: "before", label: `${n} ${unit} before the event` };
}

// ── NAMING AN INCIDENT IN ONE LINE A NURSE CAN PICK FROM ────────────────────
//
// This read `[date, type, nature, injury].filter(Boolean).join(" · ")`, and on a live
// tenant it produced
//
//     2026-09-02 · wound · Skin tear · true
//
// `injury` is not one type. The engine projects `pay.injury ?? pay.injuryType`, and
// PointClickCare fills the first as a BOOLEAN and the second as a STRING, so the field
// arrives as `true`, as `"Skin tear"`, or as null depending on the tenant and the
// record. `filter(Boolean)` keeps `true` and `join` stringifies it, so a compliance
// screen offered the word "true" as a fact about a resident's injury.
//
// A boolean becomes the words it means. A string is already words. `false` and absent
// are collapsed HERE AND ONLY HERE: this label is an identifier for choosing one row
// out of five, not a clinical claim, and appending "no injury recorded" to every
// uninjured incident is noise on the one line that has to stay scannable. The
// distinction survives in the data the audit is measured against.
export function incidentLabel(i) {
  const parts = [String(i?.incident_date || "").slice(0, 10), i?.type, i?.nature];
  const inj = i?.injury;
  if (inj === true) parts.push("with injury");
  else if (typeof inj === "string" && inj.trim()) parts.push(inj.trim());
  return parts.filter((x) => typeof x === "string" && x.trim()).map((x) => x.trim()).join(" · ");
}

const SuggestionCard = ({ s, onAccept, onDismiss, busy, readOnly, reach = null, eventDate = null }) => {
  const [showContext, setShowContext] = React.useState(false);
  const position = positionAgainstEvent(s.sourceDate, eventDate);
  const gap = s.kind === "GAP";
  const decided = s.status !== "PROPOSED";
  const tone = decided
    ? { bg: "var(--ed-bg)", border: "var(--ed-line)", fg: "var(--ed-muted)" }
    : gap
      ? { bg: "var(--ed-amber-soft)", border: "var(--ed-amber-line)", fg: "#78350f" }
      : { bg: "var(--ed-violet-soft)", border: "var(--ed-violet-line)", fg: "#4c1d95" };

  return (
    <div
      style={{
        marginTop: 7,
        padding: "8px 11px",
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: 8,
        fontSize: T.sm,
        color: tone.fg,
        lineHeight: LH.body,
        opacity: decided ? 0.72 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
        {gap ? (
          <span
            className={decided ? undefined : "pia-gap-mark"}
            title="Nothing in the readable record supports this row"
            style={{
              display: "inline-block",
              width: 18,
              height: 18,
              lineHeight: "18px",
              textAlign: "center",
              borderRadius: 9,
              background: "var(--ed-amber)",
              color: "var(--ed-canvas)",
              fontWeight: 900,
              fontSize: T.sm,
            }}
          >
            !
          </span>
        ) : (
          <span style={{ fontWeight: 800, fontSize: T.xs, letterSpacing: ".04em" }}>FROM THE CHART</span>
        )}
        <strong>{gap ? "Nothing supports this row" : `Suggests ${s.proposedState}`}</strong>
        {/* "medium confidence" of WHAT. Rule 4 of the prompt already asks the model to
            notice when the chart holds the adjacent fact rather than the one asked — "a
            turning programme is not a restraint-release record" — and that reasoning was
            computed and thrown away, leaving a bare word nobody can act on. */}
        {s.confidence ? (
          <span style={{ fontSize: T.xs, opacity: 0.8 }}>
            · {s.confidence} confidence{s.confidenceWhy ? `: ${s.confidenceWhy}` : ""}
          </span>
        ) : null}
        {/* A NOTE ID IS NOT SOMETHING A PERSON CAN FIND. This said "NOTE 8a3f21".
            Note search shows her "Nursing Weekly Summary · M. Santos · 8/30/2026",
            which is what she would actually use to find it — and the server already
            had that in hand when the suggestion was built. The id stays in the
            tooltip, because it is what a support conversation needs. */}
        {s.sourceLabel || s.sourceRef ? (
          <span
            style={{ fontSize: T.xs, opacity: 0.8 }}
            title={s.sourceRef ? `${s.sourceKind || "source"} ${s.sourceRef}` : undefined}
          >
            · {s.sourceLabel || `${s.sourceKind || "source"} ${s.sourceRef}`}
            {!s.sourceLabel && s.sourceDate ? ` (${s.sourceDate})` : ""}
          </span>
        ) : null}
        {/* BEFORE OR AFTER, in words. "11 days before the event" tells a nurse
            instantly whether this can possibly answer the row she is on — and the
            screen does not have to encode a clinical judgement per row to be useful.
            Amber on pre-event evidence because that is the direction that misleads:
            post-event evidence on a prior-to row reads as obviously wrong, pre-event
            evidence on a response row reads as fine. */}
        {position ? (
          <span
            style={{
              fontSize: T.xs, fontWeight: 700,
              color: position.side === "before" ? "var(--ed-amber)" : "var(--ed-ink-3)",
            }}
          >
            · {position.label}
          </span>
        ) : null}
        {decided ? (
          <span style={{ fontSize: T.xs, fontWeight: 700 }}>
            · {s.status === "ACCEPTED" ? "accepted" : "dismissed"}
            {s.decidedBy ? ` by ${s.decidedBy}` : ""}
          </span>
        ) : null}
      </div>

      {/* VERBATIM, AND MARKED AS SUCH. A nurse accepting this is attesting to the
          chart, and they cannot do that from a paraphrase. */}
      {s.quote ? (
        <blockquote
          style={{
            margin: "6px 0 0",
            padding: "6px 10px",
            background: "rgba(255,255,255,.75)",
            borderLeft: "3px solid rgba(0,0,0,.18)",
            borderRadius: 4,
            fontStyle: "italic",
            whiteSpace: "pre-wrap",
          }}
        >
          {/* ── A SENTENCE LIFTED OUT OF A NOTE CANNOT BE JUDGED ON ITS OWN ──────
              Accepting this writes it in as HER finding, so she has to be able to
              tell whether it means what it appears to mean — and that meant leaving
              the worksheet to go and find the note. The surrounding text was already
              in hand at pull time. Dimmed, so what is quoted stays unambiguous: the
              dark text is the quote and nothing else. */}
          {showContext && s.quoteBefore ? (
            <span style={{ opacity: 0.55, fontStyle: "normal" }}>…{s.quoteBefore}</span>
          ) : null}
          {s.quote}
          {showContext && s.quoteAfter ? (
            <span style={{ opacity: 0.55, fontStyle: "normal" }}>{s.quoteAfter}…</span>
          ) : null}
        </blockquote>
      ) : null}
      {s.quote && (s.quoteBefore || s.quoteAfter) ? (
        <button
          type="button"
          onClick={() => setShowContext((v) => !v)}
          style={{
            marginTop: 4, padding: 0, border: "none", background: "none", cursor: "pointer",
            fontSize: T.xs, color: "inherit", opacity: 0.75, textDecoration: "underline", fontFamily: "inherit",
          }}
        >
          {showContext ? "hide what is around it" : "show what is around it"}
        </button>
      ) : null}

      <div style={{ marginTop: 5 }}>{s.rationale}</div>
      {s.shouldBe ? (
        <div style={{ marginTop: 5 }}>
          <strong>What should be there:</strong> {s.shouldBe}
        </div>
      ) : null}
      {/* HOW FAR THE SEARCH REACHED, ON THE FINDING ITSELF. This was a line shown once
          after the pull and then gone. "Nothing supports this row" reads very
          differently when only 60 of 200 notes were read, and an hour later there was
          nothing on screen to say so. Only on a gap: on evidence, the quote is the
          point and the reach is noise. */}
      {s.kind === "GAP" && reach ? (
        <div style={{ marginTop: 5, fontSize: T.xs, opacity: 0.8 }}>{reach}</div>
      ) : null}

      {!decided && !readOnly ? (
        <div style={{ display: "flex", gap: 7, marginTop: 8, flexWrap: "wrap" }}>
          {!gap ? (
            <button
              type="button"
              disabled={busy}
              onClick={onAccept}
              title={`Record ${s.proposedState} on this row, as your answer, with this quote attached`}
              style={{
                padding: "4px 10px",
                borderRadius: 7,
                fontWeight: 700,
                fontSize: T.sm,
                border: "1px solid #4c1d95",
                background: "#4c1d95",
                color: "var(--ed-canvas)",
                cursor: busy ? "default" : "pointer",
              }}
            >
              Use as my answer
            </button>
          ) : null}
          <button
            type="button"
            disabled={busy}
            onClick={onDismiss}
            style={{ padding: "4px 10px", borderRadius: 7, fontSize: T.sm, cursor: busy ? "default" : "pointer" }}
          >
            Dismiss
          </button>
        </div>
      ) : null}
    </div>
  );
};

/**
 * ELEVEN QUESTIONS THAT HAVE TO BE SCANNED, NOT READ.
 *
 * This was a vertical stack: each row a block of question, source, a full paragraph
 * of reasoning, a picker and a note field. Eleven of those is a page you scroll
 * through twice and cannot hold in your head — and the thing a nurse is doing with
 * this sheet is comparing rows ("which ones are still open?"), which a stack makes
 * impossible.
 *
 * So the default shape is a GRID, one line per indicator, aligned columns:
 *
 *   #   question · source        record    your answer        what you found
 *
 * The reasoning is the bulk and it is not what you scan for, so it collapses behind
 * the record chip. It is one click away, never gone, and it opens automatically when
 * the row is one a person has to act on — a gap, a suggestion, or a disagreement —
 * because those are the rows where the prose IS the work.
 */
const IndicatorRow = ({ indicator, row, draft, onDraft, readOnly, suggestions = [], onAccept, onDismiss, busy, pullExamined = null, pullReach = null, pullTruncated = false, eventDate = null }) => {
  const entered = draft?.state !== undefined ? draft.state : row?.entered?.state ?? null;
  const note = draft?.note !== undefined ? draft.note : row?.entered?.note ?? "";
  const sysState = row?.system?.state ?? "?";
  const disagrees = entered && sysState !== "?" && sysState !== entered;
  const open = suggestions.some((sg) => sg.status === "PROPOSED");

  // OPEN BY DEFAULT ONLY WHERE THE PROSE IS THE WORK. An N is a gap the sheet is
  // asserting and the reason is the assertion; a disagreement needs both sides
  // visible; a suggestion is a thing to accept or dismiss. Everything else —
  // including all eleven rows of an N/A sheet — starts closed, which is the whole
  // point of the change.
  const mustRead = sysState === "N" || disagrees || open;
  const [show, setShow] = useState(mustRead);
  // Reopens when a row BECOMES actionable (a suggestion arrives, an answer is typed
  // that disagrees) without fighting a person who has closed it themselves.
  const wasMustRead = useRef(mustRead);
  useEffect(() => {
    if (mustRead && !wasMustRead.current) setShow(true);
    wasMustRead.current = mustRead;
  }, [mustRead]);

  return (
    <div className="pia-row" style={{ borderTop: "1px solid #e9eef5", padding: "13px 0" }}>
      <div
        style={{
          display: "grid",
          // n | question | record chip | picker | note.  Collapses to two columns on a
          // narrow screen via the stylesheet below, rather than scrolling sideways.
          gridTemplateColumns: PIA_GRID_COLUMNS,
          gap: "0 12px",
          alignItems: "center",
        }}
      >
        <div style={{ color: "var(--ed-muted-2)", fontWeight: 800, fontSize: T.base }}>{indicator.n}</div>

        <div style={{ minWidth: 0 }}>
          <div style={{ color: "var(--ed-ink)", fontWeight: 600, lineHeight: LH.tight, fontSize: T.md }}>{indicator.text}</div>
          <div style={{ color: "var(--ed-muted-2)", fontSize: T.xs, marginTop: 2 }}>
            {indicator.source}
            {indicator.neverCheckable ? " · not readable by software" : ""}
          </div>
        </div>

        {/* The record's own answer doubles as the disclosure control, because what a
            reader wants after seeing the chip is why. */}
        {row?.system ? (
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            title={show ? "Hide what the record shows" : "Why does the record read this way?"}
            aria-expanded={show}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", lineHeight: 1 }}
          >
            <Chip state={sysState} title="What the record shows" />
          </button>
        ) : (
          <div />
        )}

        <StatePicker value={entered} onChange={(s) => onDraft({ state: s })} disabled={readOnly} />

        {/* ── A SIGNED WORKSHEET HID ITS OWN FINDINGS ─────────────────────────────
            A single-line <input> clips: "Wedge cushion + toileting program trialed
            08/27, d…" with no way to see the rest, because the field is disabled and
            cannot even be scrolled with a caret.

            While the audit is OPEN that is survivable -- the nurse typed it and can
            click in. Once it is SIGNED it is the record: the thing a surveyor is handed
            and the thing the PDF is rendered from, and the longest findings are exactly
            the ones that matter. So read-only rows render the note as TEXT that wraps.

            Deliberately not "make the input wider" or "use a textarea": the column is
            fixed by the grid and a disabled textarea clips the same way, just taller. */}
        {readOnly ? (
          <div
            style={{
              minWidth: 0,
              padding: "8px 10px",
              borderRadius: 7,
              border: "1px solid #eef2f7",
              background: "var(--ed-bg)",
              fontSize: T.sm,
              lineHeight: LH.body,
              color: note ? "var(--ed-ink)" : "var(--ed-muted-2)",
              // Long chart references and URLs have no spaces to break at.
              overflowWrap: "anywhere",
            }}
          >
            {note || "—"}
          </div>
        ) : (
        <input
          type="text"
          value={note}
          disabled={readOnly}
          onChange={(e) => onDraft({ note: e.target.value })}
          placeholder="What you found, and where"
          style={{
            width: "100%",
            minWidth: 0,
            // BORDER-BOX, OR THE FIELD OVERFLOWS ITS OWN COLUMN. With content-box,
            // width:100% is the CONTENT width and the padding and border are added on
            // top -- 22px of overflow here -- so the input ran under the "reads as" chip
            // to its right and clipped whatever the nurse had typed. It was 6px of
            // overlap before the type scale and 10px after, which is how it was found:
            // the measurement moved with the padding, so it was arithmetic, not layout.
            boxSizing: "border-box",
            // See the font note by `aspectTitle`: controls do not inherit it.
            fontFamily: "inherit",
            padding: "8px 10px",
            borderRadius: 7,
            border: "1px solid var(--ed-line)",
            fontSize: T.sm,
            color: "var(--ed-ink)",
          }}
        />
        )}

        {/* WHAT THE ROW READS AS, which is not either of the two columns before it:
            the record's answer, overridden by the person's where they gave one. It is
            the column the percentage is computed from, and the one a reader scans
            down to see where the sheet stands.

            It reads as a MARK and not as a letter, and a Y answered by the record and a Y
            answered by the nurse are two different marks. "9 of 11 met" means two
            different things depending on which, a surveyor cares which, and the reader
            should not have to look two columns left to find out. */}
        <div style={{ textAlign: "center" }}>
          <RowMark
            entered={entered}
            systemState={row?.system ? sysState : null}
            onToggle={row?.system ? () => setShow((v) => !v) : null}
            expanded={show}
          />
        </div>
      </div>

      {/* The detail, on the same indent as the question so it reads as belonging to
          it rather than to the sheet. */}
      {show && row?.system ? (
        <div
          style={{
            margin: "7px 0 0 34px",
            padding: "7px 10px",
            background: "var(--ed-bg)",
            border: "1px solid #eef2f7",
            borderRadius: 8,
            fontSize: T.sm,
            color: "var(--ed-ink-3)",
            lineHeight: LH.body,
          }}
        >
          <span style={{ color: "var(--ed-muted)", fontWeight: 700 }}>from the record:</span> {row.system.because}
          {row.system.evidence?.notes?.length ? (
            <div style={{ color: "var(--ed-info-deep)", marginTop: 4 }}>
              Notes to read: {row.system.evidence.notes.map((n) => n.date).join(" · ")}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* NEVER COLLAPSED. A disagreement is the sheet and the nurse saying different
          things about the same row, and it has to be visible without a click. */}
      {disagrees ? (
        <div style={{ margin: "6px 0 0 34px", fontSize: T.sm, color: "var(--ed-amber)", lineHeight: LH.body }}>
          Your answer differs from the record ({sysState} → {entered}). That is often right — the evidence can be
          real and not where we can read it. It stays on the sheet as a difference rather than replacing the record.
        </div>
      ) : null}

      {row?.entered ? (
        <div style={{ margin: "5px 0 0 34px", fontSize: T.xs, color: "var(--ed-muted-2)" }}>
          Entered by {row.entered.by} on {String(row.entered.at).slice(0, 10)}
        </div>
      ) : null}

      <div style={{ marginLeft: 34 }}>
        {suggestions.map((sg) => (
          <SuggestionCard
            key={sg.id}
            s={sg}
            busy={busy}
            readOnly={readOnly}
            reach={pullReach}
            eventDate={eventDate}
            onAccept={() => onAccept(sg)}
            onDismiss={() => onDismiss(sg)}
          />
        ))}

        {/* ── EXAMINED AND NOTHING FOUND IS NOT THE SAME AS NEVER EXAMINED ──────
            The prompt asks for FEW proposals, so most rows come back with nothing
            against them, and the screen drew the same blank whether the chart had been
            read for this row or never read at all. Those are opposite facts: the first
            is weak evidence toward a gap, the second is evidence of nothing.

            Drawn quietly -- it is on most rows after a pull, and a loud note on every
            row is a note nobody reads. The truncation caveat rides with it, because
            "nothing was found" means much less when the search was cut short. */}
        {pullExamined === true && !suggestions.length ? (
          <div style={{ marginTop: 6, fontSize: T.xs, color: "var(--ed-muted-2)", lineHeight: LH.body }}>
            {/* TERSE ON PURPOSE. The first version repeated the whole reach sentence
                here -- "Read 40 note(s) and 3 assessment(s) on 2026-10-04. More notes
                exist than were read..." -- and it lands on most rows after a pull, so
                six rows in a column each recited it. A caveat repeated six times is a
                caveat nobody reads. The full sentence stays where it changes the
                meaning: on a GAP, and once in the header. Here it is five words. */}
            The chart was read for this row and nothing was found.
            {pullTruncated ? " Not every note was read." : ""}
          </div>
        ) : null}
        {pullExamined === false ? (
          <div style={{ marginTop: 6, fontSize: T.xs, color: "var(--ed-amber)", lineHeight: LH.body }}>
            This row was not examined by the last chart read.
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * @param {object} p
 * @param {object} [p.sample]  a resident out of the worked-example fixture. When
 *   present NOTHING is fetched and nothing can be written: the sheet is the example.
 *   A sample worksheet that accepted edits would either lose them silently or POST a
 *   resident id that does not exist, and both read as the product being broken.
 */
/**
 * The printable worksheet. Hidden on screen, visible only to the printer.
 *
 * Built from the same merged rows the screen renders, so it cannot drift from what the
 * nurse saw — a print view assembled from a second source is a document that disagrees
 * with the screen it was printed from.
 */
const PrintSheet = ({ d, aspectTitle, residentId, residentName, liveCoverage, reviewer, reviewedOn, comments, suggestionsByN }) => {
  // PASSED IN, NOT RECOMPUTED. The printed header and the screen's title have to be
  // the same string -- a paper worksheet naming a different audit than the screen it
  // was printed from is the version a surveyor keeps.
  const indicators = d.indicators || [];
  const rowsByN = new Map((d.rows || []).map((r) => [r.n, r]));
  const c = liveCoverage || {};
  const pct = c.compliance == null ? null : Math.round(c.compliance * 1000) / 10;

  return (
    <div id="pia-print">
      <h2 style={{ margin: 0, fontSize: "14pt" }}>Performance Improvement Monitoring Worksheet</h2>
      <div style={{ fontSize: "11pt", marginTop: "2pt" }}>
        <strong>Aspect of Care:</strong> {aspectTitle} &nbsp;·&nbsp;
        <strong>Method of Review:</strong> {d.methodOfReview} &nbsp;·&nbsp;
        <strong>Threshold:</strong> {Math.round((d.threshold ?? 0.95) * 100)}%
      </div>
      <div style={{ fontSize: "11pt", marginTop: "4pt" }}>
        <strong>Resident:</strong> {residentName || residentId} &nbsp;·&nbsp;
        <strong>Reviewer:</strong> {reviewer || "________________"} &nbsp;·&nbsp;
        <strong>Date Reviewed:</strong> {reviewedOn || "____________"}
      </div>
      {d.compiled ? (
        <div style={{ fontSize: "10pt", marginTop: "3pt" }}>
          Record read {d.compiled.asOf}
          {d.compiled.itemSetCode ? ` (${d.compiled.itemSetCode})` : ""}
          {d.compiled.devices?.length
            ? ` · Coded: ${d.compiled.devices.map((x) => `${x.code} ${x.label}`).join("; ")}`
            : ""}
        </div>
      ) : null}

      {/* THE HEADLINE PRINTS WITH ITS COVERAGE, exactly as it does on screen. A printed
          percentage with no denominator is the version that ends up in a binder. */}
      <div style={{ fontSize: "11pt", marginTop: "8pt" }}>
        <strong>% Compliance:</strong>{" "}
        {pct == null ? "— (nothing answered)" : `${pct}% of ${c.verified} answered row(s)`}
        {" · "}
        {c.fromData || 0} verified from the record, {c.fromPerson || 0} entered by the reviewer,{" "}
        {c.unknown || 0} still to check
        {c.notApplicable ? `, ${c.notApplicable} N/A` : ""}
        {". "}
        The {Math.round((d.threshold ?? 0.95) * 100)}% threshold applies to all {c.total ?? indicators.length} indicators.
      </div>

      <table className="pia-print-grid">
        <thead>
          <tr>
            <th style={{ width: "22pt" }}>#</th>
            <th>Indicator / Criteria</th>
            <th className="state">Y/N/NA</th>
          </tr>
        </thead>
        <tbody>
          {indicators.map((ind) => {
            const r = rowsByN.get(ind.n);
            const eff = r?.effective ?? "?";
            return (
              <tr key={ind.n}>
                <td>{ind.n}</td>
                <td>{ind.text}</td>
                {/* A LETTER, NOT A COLOUR. Photocopied in black and white, a colour-coded
                    Y and N are the same cell. "—" for a row nobody has answered. */}
                <td className="state">{eff === "?" ? "—" : eff}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {comments ? (
        <div style={{ fontSize: "10pt", marginTop: "8pt" }}>
          <strong>Comments:</strong> {comments}
        </div>
      ) : null}

      {/* THE SECOND PAGE IS THE POINT. A sheet of letters with nothing behind them is
          what this tool exists to replace. */}
      <div className="pia-detail">
        <h2 style={{ fontSize: "13pt", margin: 0 }}>Basis for each row</h2>
        <div style={{ fontSize: "10pt", marginTop: "3pt" }}>
          {aspectTitle} · {residentName || residentId} · reviewed {reviewedOn || "—"}
        </div>
        {indicators.map((ind) => {
          const r = rowsByN.get(ind.n);
          const eff = r?.effective ?? "?";
          const sgs = (suggestionsByN.get(ind.n) || []).filter((g) => g.status === "PROPOSED");
          return (
            <div key={ind.n} className="pia-row-detail">
              <h3>
                {ind.n}. {ind.text} — <span style={{ textDecoration: "underline" }}>{eff === "?" ? "not answered" : eff}</span>
              </h3>
              <div>
                <strong>Expected source:</strong> {ind.source}
                {ind.neverCheckable ? " (no readable source in this integration)" : ""}
              </div>
              {r?.system?.because ? (
                <div>
                  <strong>From the record:</strong> {r.system.because}
                </div>
              ) : null}
              {r?.entered ? (
                <div>
                  <strong>Reviewer ({r.entered.by}, {String(r.entered.at).slice(0, 10)}):</strong>{" "}
                  {r.entered.state}
                  {r.entered.note ? ` — ${r.entered.note}` : ""}
                </div>
              ) : null}
              {r?.disagrees ? (
                <div>
                  <strong>Note:</strong> the reviewer's answer differs from the record ({r.system.state} → {r.entered.state}).
                </div>
              ) : null}
              {sgs.map((g) => (
                <div key={g.id}>
                  <strong>{g.kind === "GAP" ? "Gap not yet resolved:" : "Undecided suggestion:"}</strong>{" "}
                  {g.shouldBe || g.rationale}
                  {g.quote ? <div className="pia-quote">“{g.quote}”</div> : null}
                </div>
              ))}
            </div>
          );
        })}

        <div className="pia-sig">
          <div />
          <div style={{ fontSize: "9pt", border: "none", width: "auto", marginTop: "2pt" }}>
            Reviewer signature — {reviewer || "name"} , {reviewedOn || "date"}
          </div>
        </div>
      </div>
    </div>
  );
};

// THE COMPONENT IS NOT RESTRAINT-SPECIFIC AND ITS NAME IS. Every row it renders comes
// from the server's `indicators`, every cell from `rows`, every heading from
// `aspectOfCare` — so it already drew whatever worksheet it was handed. The name is
// kept because renaming a file in this repository has already, once, rewritten an
// import and an assertion in lockstep so that a test passed on code that could not
// compile. It is a file name, and the aspect it is showing is a prop.

// ── THE NOT-READABLE-YET BLOCK ────────────────────────────────────────────────
//
// Deliberately styled as unfinished business: dashed border, amber, and the word PREVIEW
// before anything else. It must not be mistakable for the worksheet above it at a glance,
// across a room, in a screenshot, or by somebody scrolling past.
const PreviewBlock = ({ preview }) => {
  const cp = preview.carePlan;
  const labs = preview.labs || [];
  const breaches = preview.parameterBreaches || [];
  const rows = preview.rows || [];
  const Badge = ({ children, tone = "amber" }) => (
    <span
      style={{
        display: "inline-block", padding: "1px 7px", borderRadius: 5,
        background: tone === "dated" ? "#dbeafe" : "#fef3c7",
        border: `1px solid ${tone === "dated" ? "#93c5fd" : "#fcd34d"}`,
        color: tone === "dated" ? "#1e3a8a" : "#78350f",
        fontSize: T.xs, fontWeight: 700, letterSpacing: 0.3, whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
  // WHEN IT STARTS READING, OR THAT NOBODY KNOWS. Three states, because "blocked",
  // "reads from November 2026" and "nothing will ever answer this" are three different
  // things to tell a nurse and only one of them is a roadmap item. An empty space here
  // would be read as "soon", which is the one meaning none of them has.
  const When = ({ e }) =>
    e.grantable === false ? (
      <Badge>never — no endpoint exposes it</Badge>
    ) : e.availableFrom ? (
      <Badge tone="dated">reads from {e.availableFrom}</Badge>
    ) : (
      <Badge>no date set</Badge>
    );
  return (
    <div
      style={{
        marginTop: 18, padding: "12px 14px", borderRadius: 10,
        border: "2px dashed #f59e0b", background: "var(--ed-amber-soft)",
      }}
    >
      <div style={{ fontSize: T.base, color: "#78350f", lineHeight: LH.prose }}>
        <Badge>PREVIEW — NOT READ FROM A CHART</Badge>{" "}
        <strong>This is what these rows would say once PointClickCare grants the scope.</strong>{" "}
        Everything below is invented and none of it touches the cells above. Waiting on:{" "}
        {preview.waitingOn.join(", ")}.
        {/* The date goes in the header too, because that is the sentence a prospect
            repeats afterwards -- "the care plan part switches on in November" is a
            roadmap; "it's blocked" is a shrug. */}
        {(() => {
          const dated = [cp, ...labs, ...breaches, ...rows]
            .filter((e) => e && e.grantable !== false && e.availableFrom);
          if (!dated.length) return null;
          const months = [...new Set(dated.map((e) => e.availableFrom))];
          return (
            <>
              {" "}
              <strong>
                {months.length === 1
                  ? `The ${[...new Set(dated.map((e) => e.scopeLabel.toLowerCase()))].join(" and ")} rows start reading in ${months[0]}.`
                  : `Scheduled grants: ${months.join(", ")}.`}
              </strong>
            </>
          );
        })()}
      </div>

      {cp ? (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #fcd34d" }}>
          <div style={{ fontSize: T.base, fontWeight: 700, color: "#78350f" }}>
            The fall care plan <Badge>{cp.scopeLabel}</Badge> <When e={cp} />
          </div>
          <div style={{ marginTop: 6, fontSize: T.sm, color: "#422006", lineHeight: LH.body }}>
            <div><strong>Focus:</strong> {cp.focus}</div>
            <div style={{ marginTop: 3 }}><strong>Goal:</strong> {cp.goal}</div>
            {/* THE GOAL CONCERN IS THE JUDGEMENT, NOT A VERDICT. An absent concern is not a
                tick -- it means nothing was raised, which is a third state. */}
            {cp.goalConcern ? (
              <div
                style={{
                  marginTop: 6, padding: "7px 9px", borderRadius: 7, background: "#fff7ed",
                  border: "1px solid #fdba74", color: "#7c2d12",
                }}
              >
                <strong>Is this goal realistic for this resident?</strong> {cp.goalConcern}
              </div>
            ) : null}
            <div style={{ marginTop: 6 }}>
              <strong>Revised:</strong> {cp.revisedOn || "—"} ·{" "}
              <strong>Progress note documenting the revision:</strong>{" "}
              {cp.progressNoteOn ? (
                cp.progressNoteOn
              ) : (
                <span style={{ color: "var(--ed-alert-deep)", fontWeight: 650 }}>none</span>
              )}
              {cp.cause ? (
                <>
                  {" · "}
                  <strong>Cause identified:</strong> {cp.cause}
                </>
              ) : null}
            </div>
          </div>

          {cp.interventions?.length ? (
            <div style={{ marginTop: 9, overflowX: "auto" }}>
              <div style={{ fontSize: T.sm, fontWeight: 650, color: "#78350f", marginBottom: 4 }}>
                Interventions, and whether each one answers this fall
              </div>
              {cp.interventions.map((i, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex", gap: 9, alignItems: "flex-start", padding: "6px 0",
                    borderTop: idx ? "1px solid var(--ed-amber-line)" : "none",
                  }}
                >
                  <div style={{ flex: "0 0 86px", fontSize: T.sm, color: "#422006", fontVariantNumeric: "tabular-nums" }}>
                    {i.initiated}
                  </div>
                  <div style={{ flex: "1 1 auto", fontSize: T.sm, color: "#422006", lineHeight: LH.body }}>
                    <div>
                      {i.text}{" "}
                      {i.isNew ? null : (
                        <span style={{ color: "var(--ed-alert-deep)", fontWeight: 650 }}>— pre-existing, not added after this fall</span>
                      )}
                    </div>
                    {i.why ? (
                      <div style={{ marginTop: 2, fontSize: T.xs, color: i.addressesCause ? "#3f6212" : "var(--ed-alert-deep)" }}>
                        {i.addressesCause ? "Addresses the cause: " : "Does not address the cause: "}
                        {i.why}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {labs.length ? (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #fcd34d" }}>
          <div style={{ fontSize: T.base, fontWeight: 700, color: "#78350f" }}>
            Labs and the follow-through <Badge>{labs[0].scopeLabel}</Badge> <When e={labs[0]} />
          </div>
          {labs.map((l, idx) => (
            <div key={idx} style={{ marginTop: 6, fontSize: T.sm, color: "#422006", lineHeight: LH.body }}>
              <div>
                <strong>{l.test}</strong> {l.value} on {l.drawnOn}{" "}
                {l.abnormal ? <span style={{ color: "var(--ed-alert-deep)", fontWeight: 700 }}>ABNORMAL</span> : "within range"}
              </div>
              <div style={{ marginTop: 3 }}>
                Physician notified: <Mark v={l.physicianNotified} /> · Family notified:{" "}
                <Mark v={l.familyNotified} />
              </div>
              {l.furtherSteps ? <div style={{ marginTop: 3, fontSize: T.xs }}>{l.furtherSteps}</div> : null}
            </div>
          ))}
        </div>
      ) : null}

      {breaches.length ? (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #fcd34d" }}>
          <div style={{ fontSize: T.base, fontWeight: 700, color: "#78350f" }}>
            Given outside its parameters <Badge>{breaches[0].scopeLabel}</Badge> <When e={breaches[0]} />
            {breaches[0].alsoNeeds?.length ? <> <Badge>+ vital signs</Badge></> : null}
          </div>
          {breaches.map((b, idx) => (
            <div key={idx} style={{ marginTop: 6, fontSize: T.sm, color: "#422006", lineHeight: LH.body }}>
              <div><strong>{b.drug}</strong> — {b.sig}</div>
              <div style={{ marginTop: 3, color: "var(--ed-alert-deep)", fontWeight: 650 }}>{b.finding}</div>
              {/* NEEDS TWO GRANTS, NOT ONE, and the screen says so rather than implying the
                  administration record alone would do it. */}
              <div style={{ marginTop: 3, fontSize: T.xs }}>
                This one needs both the administration record and vital signs: the dose and the blood pressure are
                separate facts in the chart, and the finding is that nothing compares them.
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {rows.length ? (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #fcd34d" }}>
          <div style={{ fontSize: T.base, fontWeight: 700, color: "#78350f", marginBottom: 5 }}>
            What the rows above would read
          </div>
          {rows.map((r, idx) => (
            <div
              key={idx}
              style={{
                display: "flex", gap: 9, alignItems: "flex-start", padding: "6px 0",
                borderTop: idx ? "1px solid var(--ed-amber-line)" : "none", fontSize: T.sm,
                color: "#422006", lineHeight: LH.body,
              }}
            >
              <div style={{ flex: "0 0 auto", fontWeight: 700 }}>Row {r.n}</div>
              <div
                style={{
                  flex: "0 0 auto", padding: "0 7px", borderRadius: 5, fontWeight: 700,
                  background: r.wouldRead === "Y" ? "#dcfce7" : r.wouldRead === "N" ? "#fee2e2" : "var(--ed-line)",
                  color: r.wouldRead === "Y" ? "#14532d" : r.wouldRead === "N" ? "#7f1d1d" : "var(--ed-ink-2)",
                }}
              >
                would read {r.wouldRead}
              </div>
              <div style={{ flex: "1 1 auto" }}>
                {r.because} <When e={r} />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// Three states, not two. `null` is "not established", which is neither yes nor no — the
// distinction the whole worksheet is built on.
const Mark = ({ v }) =>
  v === true ? (
    <span style={{ color: "#14532d", fontWeight: 700 }}>yes</span>
  ) : v === false ? (
    <span style={{ color: "var(--ed-alert-deep)", fontWeight: 700 }}>no</span>
  ) : (
    <span style={{ color: "#78350f", fontWeight: 650 }}>not established</span>
  );

export default function RestraintWorksheet({ ccn, aspect = "RESTRAINTS", incidentId: incidentIdProp = null, residentId, residentName, triggerKind = "ROUTINE", triggerRef = null, sample = null, onClose }) {
  const [state, setState] = useState({ loading: true });
  const [drafts, setDrafts] = useState({});
  const [reviewer, setReviewer] = useState("");
  const [reviewedOn, setReviewedOn] = useState(new Date().toISOString().slice(0, 10));
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // When the sample answers on this device were last kept, so the banner can say.
  const [sampleSavedAt, setSampleSavedAt] = useState(null);
  // The chart pull. Declared here, in the component that renders it.
  const [incidents, setIncidents] = useState(null); // null = not fetched yet
  // THE EVENT THIS AUDIT IS OF. Seeded from the launcher, which is where it is chosen
  // now: migration 066 keys the open-review slot on it, so it decides WHICH worksheet
  // this is, not merely which event the chart-pull should focus on. The select below
  // still lets it be narrowed for the pull, and starts on the audit's own event rather
  // than blank — they are the same fact and showing two of them invites disagreement.
  const [incidentId, setIncidentId] = useState(incidentIdProp ? String(incidentIdProp) : "");
  const [pullRun, setPullRun] = useState(null);

  const load = useCallback(async () => {
    // THE SAMPLE NEVER TOUCHES THE NETWORK. Shaped exactly like the API's own
    // response so the render path below is the same one live data takes — a demo
    // rendered by a second code path is a demo that can look right while the product
    // is wrong, which is the whole reason the fixture is generated from the compiler.
    if (sample) {
      setState({
        loading: false,
        data: {
          aspectOfCare: SAMPLE.aspectOfCare,
          methodOfReview: SAMPLE.methodOfReview,
          threshold: SAMPLE.threshold,
          indicators: SAMPLE.indicators,
          rows: sample.rows,
          review: sample.review,
          coverage: sample.coverage,
          // The worked example's proposals. Invented model output, passed through the
          // REAL enforcement by the generator — so a demoted row in the demo is proof
          // the gate ran, not a claim that it would.
          suggestions: sample.suggestions || [],
          compiled: {
            asOf: sample.asOf,
            itemSetCode: sample.itemSetCode,
            inUse: sample.inUse,
            devices: sample.devices,
            alarms: sample.alarms,
            worksheetNote: sample.worksheetNote ?? null,
          },
          notCompiledYet: false,
          history: [],
        },
      });
      // WHAT WAS TYPED HERE BEFORE WINS OVER THE FIXTURE. A reviewer who answered three
      // rows, closed the sheet and came back expects to find three answers; re-seeding from
      // the fixture would silently discard them, which is worse than never having kept them
      // because the first version appeared to work.
      const kept = loadSampleDraft(aspect, sample.residentId);
      setReviewer(kept?.reviewer || sample.review?.reviewer || "");
      setComments(kept?.comments || sample.review?.comments || "");
      if (kept?.reviewedOn) setReviewedOn(kept.reviewedOn);
      setDrafts(kept?.drafts || {});
      setSampleSavedAt(kept?.savedAt || null);
      return;
    }
    setState({ loading: true });
    try {
      const r = await fetchResidentWorksheet(ccn, residentId, { aspect });
      setState({ loading: false, ...r });
      if (r.data?.review) {
        setReviewer(r.data.review.reviewer || "");
        setReviewedOn(r.data.review.reviewedOn || new Date().toISOString().slice(0, 10));
        setComments(r.data.review.comments || "");
      }
      setDrafts({});
      // The event list costs no PointClickCare call — it is survai's own copy of the
      // incident feed — so it is fetched with the worksheet rather than behind a click.
      try {
        const inc = await fetchIncidents(ccn, residentId);
        setIncidents(inc.locked ? [] : inc.data?.incidents ?? []);
      } catch {
        setIncidents([]); // a missing event list must not block the worksheet
      }
    } catch (e) {
      setState({ loading: false, error: e.message });
    }
      // The aspect is a dependency for the same reason it is on the list: without it the
    // worksheet would keep showing the previous aspect's rows under the new one's name.
  }, [ccn, aspect, residentId, sample]);

  useEffect(() => {
    load();
  }, [load]);

  const d = state.data || {};
  const review = d.review || null;
  const signed = !!review?.completedAt;
  // SAMPLE DATA IS TYPEABLE NOW, and the reason it used to be read-only was true but
  // answered the wrong question. There is no review behind it to write to and no facility
  // to write against -- so nothing here goes to the server. That is not a reason to refuse
  // typing: somebody evaluating this wants to answer a row and watch the tally and the
  // data/person coverage split respond, which is the whole thing the worksheet does.
  //
  // What is typed is kept in browser storage, per device, and the banner below says so.
  // Acceptable only because these residents are invented; this path must never be pointed
  // at live data.
  //
  // `signed` still locks it, in the sample exactly as live. Two of the worked examples are
  // signed off on purpose, so the demo shows a locked sheet as well as an open one -- a
  // reviewer who cannot see what "signed" does to the screen has not been shown the
  // control that matters most here.
  const readOnly = signed;
  const sampleEditable = !!sample && !signed;

  // ── THE PICKER AND THE SHEET PRINTED TWO DIFFERENT NAMES FOR THE SAME AUDIT ──
  //
  // The picker reads POST_INCIDENT_ASPECTS ("Nursing — Physical Restraints", em dash).
  // The sheet printed `aspectOfCare` straight off the compiled worksheet, and every
  // fixture and compiler spells it with a HYPHEN ("Nursing - Physical Restraints").
  // So a nurse chose one name and the worksheet, the print header and the PDF all
  // carried a slightly different one. Nothing failed; it just read as two tools.
  //
  // The aspect KEY is the thing both sides agree on, so the label is derived from it
  // and the server's string is the fallback for an aspect this build does not know.
  // Deliberately not "fixed" in the fixtures: they are generated from the compilers,
  // so the next regeneration would put the hyphen back.
  const aspectTitle = postIncidentAspectLabel(aspect) || d?.aspectOfCare || "";


  // The coverage the header shows must include the unsaved drafts, or the nurse
  // watches a number that ignores what they just typed.
  const liveCoverage = useMemo(() => {
    const rows = d.rows || [];
    if (!rows.length) return d.coverage;
    // THROUGH rowMark.js, NOT A SECOND COPY OF THE TERNARY. The header says "6 verified
    // from the record, 2 entered by a reviewer" and each row shows its own mark; those are
    // the same claim counted twice, and when the two definitions of "who answered" lived
    // in two places they were one edit away from disagreeing on screen.
    const eff = rows.map((r) => {
      const dr = drafts[r.n];
      const entered = dr?.state !== undefined ? dr.state : r.entered?.state ?? null;
      const systemState = r.system ? r.system.state ?? "?" : null;
      return {
        effective: effectiveState({ entered, systemState }),
        by: answeredBy({ entered, systemState }),
        mark: rowMark({ entered, systemState }).mark,
        disagrees: !!(entered && r.system && r.system.state !== "?" && r.system.state !== entered),
      };
    });
    const yes = eff.filter((r) => r.effective === "Y").length;
    const no = eff.filter((r) => r.effective === "N").length;
    return {
      total: rows.length,
      verified: yes + no,
      unknown: eff.filter((r) => r.effective === "?").length,
      notApplicable: eff.filter((r) => r.effective === "NA").length,
      fromData: eff.filter((r) => r.by === "data" && (r.effective === "Y" || r.effective === "N")).length,
      fromPerson: eff.filter((r) => r.by === "person" && (r.effective === "Y" || r.effective === "N")).length,
      disagreements: eff.filter((r) => r.disagrees).length,
      compliance: yes + no ? yes / (yes + no) : null,
      // The same eleven rows counted by MARK rather than by letter, so the legend under
      // the column headers doubles as where-this-sheet-stands. The five buckets are
      // disjoint and sum to `total`; that is asserted in rowMark.test.js, because a
      // tally that quietly drops a row is how a sheet reads as finished early.
      marks: MARK_ORDER.reduce((acc, k) => ({ ...acc, [k]: eff.filter((r) => r.mark === k).length }), {}),
    };
  }, [d.rows, d.coverage, drafts]);

  // BELOW liveCoverage DELIBERATELY. A useCallback's dependency ARRAY is evaluated
  // immediately, not deferred like its body, so declaring this above the useMemo put
  // `liveCoverage` in the temporal dead zone: "Cannot access 'liveCoverage' before
  // initialization", every render, a blank screen. The production build does not catch
  // it -- the identifier resolves, it is the ORDER that is wrong -- so it survived a
  // clean `react-scripts build` and was found by loading the page.
  // ── "WHERE IS IT GOING AFTER IT IS SAVED? DO YOU ALLOW DOWNLOAD?" ────────────
  //
  // It was going into survai.post_incident_audit_review and nowhere a person could
  // reach. Print was the only way out and Print is not a file: it is a dialogue that
  // yields paper, or a PDF named after the browser tab. A facility cannot attach that
  // to a QAPI binder or send it to its corporate consultant.
  //
  // The document is built from the rows ALREADY ON SCREEN -- see postIncidentAuditDocx
  // -- so it cannot disagree with what was downloaded from.
  const [dl, setDl] = useState({ busy: false, error: null });
  // ── "ONLY WHAT WAS WRITTEN AFTER THE EVENT" ───────────────────────────────
  //
  // Off by default and deliberately so. Some rows WANT pre-event evidence -- "other
  // methods tried PRIOR TO use of the restraint" is one -- so a filter that defaulted on
  // would make those rows unanswerable while looking tidier. It is a tool for the rows
  // where it helps, not a correctness rule.
  const [afterEventOnly, setAfterEventOnly] = useState(false);
  const download = useCallback(async () => {
    setDl({ busy: true, error: null });
    try {
      const blob = await buildAuditDocx({
        d, aspectTitle, ccn, residentId, residentName,
        coverage: liveCoverage, reviewer, reviewedOn, comments, signed,
      });
      downloadBlob(blob, auditFileName({ ccn, residentName, residentId, aspectTitle, reviewedOn, signed }));
      setDl({ busy: false, error: null });
    } catch (e) {
      // SAID ON THE SCREEN, not in the console. A download that silently does nothing
      // is the failure a person reports as "the button is broken".
      setDl({ busy: false, error: e?.message || "The download could not be built." });
    }
  }, [d, aspectTitle, ccn, residentId, residentName, liveCoverage, reviewer, reviewedOn, comments, signed]);

  // EVERY FIELD THAT CAN CHANGE, or Save is disabled on a change the nurse just made.
  // reviewedOn was missing, so editing only the date left the button dead.
  const dirty =
    Object.keys(drafts).length > 0 ||
    (review &&
      (reviewer !== (review.reviewer || "") ||
        comments !== (review.comments || "") ||
        reviewedOn !== (review.reviewedOn || new Date().toISOString().slice(0, 10))));

  const begin = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await startReview(ccn, residentId, {
        triggerKind, triggerRef, aspect,
        // The audit's own event, not whatever the pull select has been narrowed to.
        incidentId: incidentIdProp || null,
      });
      if (r.locked) setMsg({ kind: "locked", text: r.reason });
      // "Resuming" rather than "started": a nurse told they started something that
      // was already half done will fill it in twice.
      else setMsg({ kind: "ok", text: r.data?.created ? "Audit started." : "Resuming the audit already open for this resident." });
      await load();
    } catch (e) {
      // AN UNFINISHED AUDIT OF A DIFFERENT EVENT IS NOT THIS ONE, AND IT ARRIVES HERE.
      //
      // send() THROWS on a non-2xx rather than returning an envelope, so this is where
      // the server's 409 audit_open_for_another_incident lands — a branch on the return
      // value would have been dead code. The server refuses rather than handing over the
      // other event's worksheet, because that is how the 3rd's answers get filed against
      // the 28th.
      //
      // Matched on the CODE, not the sentence, and the details name which event is open
      // so this can be more than "something is in the way".
      if (e.code === "audit_open_for_another_incident") {
        const openFor = e.details?.openIncidentId;
        setMsg({
          kind: "err",
          text: e.message + (openFor ? ` (the open one is for event ${openFor})` : ""),
        });
      } else setMsg({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const persist = async ({ complete = false } = {}) => {
    // ── THE SAMPLE SAVES TO THIS DEVICE, AND NOWHERE ELSE ─────────────────────
    //
    // Before every other check, because `if (!review) return` below silently did nothing
    // for the sample residents that have no review -- a Save button that is enabled and
    // returns without a word. No API call is made here: there is no facility, and the
    // whole point of the mode is that there does not need to be one.
    //
    // It reports a FAILED write rather than saying "Saved." over one that did not happen.
    // localStorage throws in a private window and silently drops on quota, and this mode
    // exists for someone trying the product out -- the person least able to tell a storage
    // refusal from a bug in the worksheet.
    if (sample) {
      const ok = saveSampleDraft(aspect, sample.residentId, { drafts, reviewer, reviewedOn, comments });
      if (ok) {
        setSampleSavedAt(new Date().toISOString());
        setMsg({
          kind: "ok",
          text: "Kept on this device. Sample answers are not saved to any facility and do not follow you to another browser.",
        });
      } else {
        setMsg({
          kind: "err",
          text:
            "This browser refused to store the answers — private browsing, or site data blocked. They are still " +
            "on screen, and will be lost when this tab closes.",
        });
      }
      return;
    }
    if (!review) return;
    setBusy(true);
    setMsg(null);
    try {
      // THREE KINDS OF DRAFT, AND THE FILTER ONLY UNDERSTOOD ONE.
      //
      // `.filter(([, v]) => v.state)` dropped everything else, and dropped it AFTER the
      // dirty flag had enabled Save — so a nurse typed a note, pressed Save, was told
      // "Saved.", and lost it. Reporting success while discarding the work is the worst
      // shape this could take.
      //
      //   answered   a state was chosen        -> write it, with whatever note is there
      //   cleared    the state was toggled off -> DELETE the row's answer (engine handles it)
      //   note-only  a note typed, no state    -> keep the row's existing state and update
      //                                           the note. With no existing entry there is
      //                                           no state to attach, and an entry cannot
      //                                           exist without one, so that is refused out
      //                                           loud instead of vanishing.
      const rows = d.rows || [];
      const entries = [];
      const orphanNotes = [];
      for (const [k, v] of Object.entries(drafts)) {
        const n = Number(k);
        const existing = rows.find((r) => r.n === n)?.entered ?? null;
        if (v.state) {
          entries.push({ n, state: v.state, note: v.note ?? existing?.note ?? null });
        } else if ("state" in v && v.state === null) {
          entries.push({ n, clear: true });
        } else if (v.note !== undefined) {
          if (existing?.state) entries.push({ n, state: existing.state, note: v.note });
          else orphanNotes.push(n);
        }
      }
      if (orphanNotes.length) {
        setMsg({
          kind: "warn",
          text:
            `Row ${orphanNotes.join(", ")}: choose Y, N or N/A before saving. A note records what you found ` +
            `for an answer, so there is nowhere to keep it until the row has one.`,
        });
        setBusy(false);
        return;
      }
      const r = await saveReview(ccn, review.id, { entries, reviewer, reviewedOn, comments, complete });
      if (r.locked) setMsg({ kind: "locked", text: r.reason });
      else setMsg({ kind: "ok", text: complete ? "Audit signed off." : "Saved." });
      await load();
    } catch (e) {
      setMsg({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const pull = async () => {
    if (!review) return;
    setBusy(true);
    setMsg(null);
    setPullRun(null);
    try {
      const r = await pullFromChart(ccn, review.id, { incidentId: incidentId || null });
      if (r.locked) setMsg({ kind: "locked", text: r.reason });
      else {
        setPullRun(r.data);
        // NOTHING FOUND AND NOTHING TO READ ARE DIFFERENT ANSWERS, and the engine
        // distinguishes them. Carrying that through matters: an empty result over an
        // empty note index reads as a clean chart.
        setMsg(
          r.data?.noNotesOnFile
            ? { kind: "warn", text: r.data.note }
            : {
                kind: "ok",
                text:
                  `Read ${r.data.notesRead} note(s): ${r.data.evidence} with something to show, ` +
                  `${r.data.gaps} gap(s).` +
                  (r.data.demoted ? ` ${r.data.demoted} proposed answer(s) were demoted to gaps — those rows have no source we can read.` : "") +
                  (r.data.truncatedNotes ? " More notes exist than were read, so a gap here is not proof of absence." : ""),
              },
        );
      }
      await load();
    } catch (e) {
      setMsg({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const decide = async (sg, how) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = how === "accept" ? await acceptSuggestion(ccn, sg.id) : await dismissSuggestion(ccn, sg.id);
      if (r.locked) setMsg({ kind: "locked", text: r.reason });
      else if (how === "accept")
        setMsg({ kind: "ok", text: `Row ${sg.n} answered ${sg.proposedState} — recorded as your entry, with the quote attached.` });
      await load();
    } catch (e) {
      setMsg({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) return <div style={{ padding: 18, color: "var(--ed-muted)" }}>Loading the worksheet…</div>;
  if (state.locked) {
    return (
      <div style={{ padding: 16, background: "var(--ed-brand-soft)", border: "1px solid var(--ed-info-line)", borderRadius: 11, color: "var(--ed-info-deep)" }}>
        <strong>In preview.</strong> {state.reason || "Restricted to the system administrator."}
      </div>
    );
  }
  if (state.error) {
    return (
      <div style={{ padding: 16, background: "var(--ed-alert-soft)", border: "1px solid #fca5a5", borderRadius: 11, color: "#7f1d1d" }}>
        {state.error} <button onClick={load} style={{ marginLeft: 10 }}>Retry</button>
      </div>
    );
  }

  const indicators = d.indicators || [];
  const rowsByN = new Map((d.rows || []).map((r) => [r.n, r]));
  // Proposals sit BESIDE their row, never inside it. Undecided first, so a row somebody
  // has already worked through does not bury the one that still needs a decision.
  const eventDate = d.pull?.eventDate ?? null;
  // HIDDEN, AND COUNTED. A filter that silently removes evidence from a compliance
  // worksheet is the same failure as a lookup against an empty table: the screen looks
  // clean and is less informed than it was. The count is rendered beside the toggle.
  let hiddenByFilter = 0;
  const suggestionsByN = new Map();
  for (const sg of d.suggestions || []) {
    if (afterEventOnly && eventDate) {
      const pos = positionAgainstEvent(sg.sourceDate, eventDate);
      // Undated evidence is NOT hidden. "We do not know when this was written" is not
      // "this was written before the event", and dropping it would be the filter
      // asserting something it cannot know.
      if (pos && pos.side === "before") { hiddenByFilter += 1; continue; }
    }
    const list = suggestionsByN.get(sg.n) || [];
    list.push(sg);
    suggestionsByN.set(sg.n, list);
  }
  for (const list of suggestionsByN.values()) {
    list.sort((a, b) => (a.status === "PROPOSED" ? 0 : 1) - (b.status === "PROPOSED" ? 0 : 1));
  }
  const openGaps = (d.suggestions || []).filter((g) => g.kind === "GAP" && g.status === "PROPOSED").length;

  // ── WHAT THE PULL COVERED, AND WHICH ROWS IT LOOKED AT ────────────────────
  //
  // Reported as "your suggestion based off what is pulled isn't exactly clear". Two of
  // the causes are here rather than in any one card.
  //
  // A row with no proposal meant two different things. The prompt asks for FEW
  // proposals, so most rows come back with nothing against them -- and "the chart was
  // read for this row and nothing was found" is the opposite fact from "this row was
  // never examined". Only one of them had a representation, so the screen drew the same
  // blank for both. d.pull.indicatorsExamined settles it per row.
  //
  // And "more notes exist than were read" was a line that appeared once after the pull
  // and then was gone. An hour later, judging a gap, the nurse had no idea the search
  // was truncated -- and "nothing supports this row" reads very differently when 60 of
  // 200 notes were read. It travels with the finding now.
  // `pull` is already the function that triggers a read, above.
  const pullRecord = d.pull || null;
  const examined = new Set((pullRecord?.indicatorsExamined || []).map(Number));
  const pullReach = pullRecord
    ? `Read ${pullRecord.notesRead} note(s)` +
      (pullRecord.assessmentsRead ? ` and ${pullRecord.assessmentsRead} assessment(s)` : "") +
      (pullRecord.ranAt ? ` on ${String(pullRecord.ranAt).slice(0, 10)}` : "") +
      (pullRecord.notesTruncated
        ? ". More notes exist than were read, so nothing found here is not proof of absence."
        : ".")
    : null;

  return (
    <div id="pia-screen" style={{ background: "var(--ed-canvas)", border: "1px solid var(--ed-line)", borderRadius: 13, padding: 18 }}>
      <style>{GAP_PULSE_CSS}</style>
      <style>{PRINT_CSS}</style>
      <PrintSheet
        d={d}
        aspectTitle={aspectTitle}
        residentId={residentId}
        residentName={residentName}
        liveCoverage={liveCoverage}
        reviewer={reviewer}
        reviewedOn={reviewedOn}
        comments={comments}
        suggestionsByN={suggestionsByN}
      />
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          {/* Middot, not an em dash: the aspect label contains an em dash of its own
              ("Nursing — Falls"), and "Nursing — Falls — Harold Pryce" reads as three
              peers rather than an audit and whose it is. */}
          <div style={{ fontWeight: 800, color: "var(--ed-ink)", fontSize: T.xl }}>
            {aspectTitle} &middot; {residentName || `Resident ${residentId}`}
          </div>
          <div style={{ color: "var(--ed-muted)", fontSize: T.base, marginTop: 3 }}>
            Method of review: {d.methodOfReview}
            {d.compiled ? ` · record read ${d.compiled.asOf}${d.compiled.itemSetCode ? ` (${d.compiled.itemSetCode})` : ""}` : ""}
          </div>
          {d.compiled?.devices?.length ? (
            <div style={{ color: "var(--ed-ink-2)", fontSize: T.base, marginTop: 5 }}>
              Coded: {d.compiled.devices.map((x) => `${x.code} ${x.label}${x.frequency ? ` — ${x.frequency}` : ""}`).join(" · ")}
            </div>
          ) : null}
          {d.compiled?.alarms?.length ? (
            <div style={{ color: "var(--ed-muted)", fontSize: T.sm, marginTop: 2 }}>
              Alarms: {d.compiled.alarms.map((x) => x.label).join(" · ")}
            </div>
          ) : null}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          <Headline coverage={liveCoverage} threshold={d.threshold ?? 0.95} />
          <div style={{ display: "flex", gap: 8 }}>
            {/* THEMED, because these two were the only unstyled controls on the page.
                Print carried a padding and a radius and no colour; Close carried nothing
                at all, so both fell through to the browser's default button chrome —
                grey, system-font, visibly not part of the product — on a screen a
                facility may be looking at beside a surveyor. #0f4c81 filled for the
                primary action and the same colour outlined for the secondary is the
                convention the rest of the app already uses. */}
            <button
              onClick={() => window.print()}
              title="Prints the worksheet grid, then a page giving the basis for every row"
              style={{
                padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: T.base,
                background: "var(--ed-brand)", color: "var(--ed-canvas)", border: "none",
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              🖨 Print
            </button>
            {/* NOT "Export". A nurse looking for her finished worksheet is looking for
                a download, and the file type is named because .docx and .pdf are
                different answers to "can I put this in our template?". */}
            <button
              onClick={download}
              disabled={dl.busy}
              title="Downloads this worksheet as a Word document, including what every row was answered from"
              style={{
                padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: T.base,
                background: "var(--ed-canvas)", color: "var(--ed-brand)", border: "1px solid var(--ed-brand)",
                cursor: dl.busy ? "default" : "pointer", fontFamily: "inherit",
                opacity: dl.busy ? 0.6 : 1,
              }}
            >
              {dl.busy ? "Building…" : "⬇ Download (Word)"}
            </button>
            {onClose ? (
              <button
                onClick={onClose}
                style={{
                  padding: "8px 16px", borderRadius: 8, fontWeight: 600, fontSize: T.base,
                  background: "var(--ed-canvas)", color: "var(--ed-brand)", border: "1px solid var(--ed-brand)",
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Close
              </button>
            ) : null}
          </div>
          {dl.error ? (
            <div role="alert" style={{ marginTop: 6, fontSize: T.sm, color: "#7f1d1d", textAlign: "right" }}>
              {dl.error}
            </div>
          ) : null}
        </div>
      </div>

      {/* WHY THIS AUDIT IS HAPPENING, and against which chart state. An audit signed
          against an older snapshot is a valid record of that cycle and no longer
          describes today's chart; a reader must not have to work that out. */}
      {review ? (
        <div style={{ marginTop: 12, fontSize: T.base, color: "var(--ed-ink-3)" }}>
          {review.triggerKind ? (
            <>
              Triggered by: <strong>{review.triggerKind.toLowerCase()}</strong>
              {review.triggerRef ? ` (${review.triggerRef})` : ""} ·{" "}
            </>
          ) : null}
          {signed ? (
            <span style={{ color: "#15803d", fontWeight: 700 }}>
              Signed by {review.reviewer} on {review.reviewedOn}
            </span>
          ) : (
            <span>in progress</span>
          )}
          {review.staleAgainst ? (
            <span style={{ color: "var(--ed-amber)" }}>
              {" "}· audited against the record as at {review.againstAsOf}; it has been re-read since ({review.staleAgainst})
            </span>
          ) : null}
        </div>
      ) : null}

      {sample ? (
        <div
          style={{
            marginTop: 12, padding: "10px 12px", background: "var(--ed-brand-soft)", border: "1px solid var(--ed-info-line)",
            borderRadius: 9, fontSize: T.base, color: "#1e3a8a", lineHeight: LH.prose,
          }}
        >
          <strong>✨ Worked example — {sample.name}, room {sample.room}.</strong> The chart is invented; every cell the
          record filled in, every reason and every figure on this sheet is real output from the same code that reads a
          live chart.{" "}
          {signed ? (
            <>
              <strong>Signed off, so it is locked</strong> — the same lock a real signed worksheet has.
            </>
          ) : (
            <>
              <strong>You can answer the rows.</strong> Nothing is sent anywhere: answers are kept in this browser
              only, not against any facility, and they will not follow you to another device.
            </>
          )}{" "}
          <em>{sample.teaches}</em>
        </div>
      ) : null}

      {d.notCompiledYet && !sample ? (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            background: "var(--ed-amber-soft)",
            border: "1px solid var(--ed-amber-line)",
            borderRadius: 9,
            fontSize: T.base,
            color: "#78350f",
            lineHeight: LH.body,
          }}
        >
          The nightly scan has not read this resident yet, so <strong>nothing</strong> on this worksheet is pre-filled —
          including whether a restraint is coded. Every row is yours to enter. This is not the same as a resident with
          no restraint.
        </div>
      ) : null}

      {/* SIGNING OFF ENDED THE RESIDENT'S AUDIT HISTORY, PERMANENTLY.
          This was `!review && !sample`, and the worksheet route returns the newest
          review — open if there is one, otherwise the most recent SIGNED one. So
          the moment an audit was signed, `review` stayed truthy forever and the
          button never came back. A resident could be audited exactly once, ever.

          On a tool whose whole premise is auditing after an event, that is the
          central case broken: a second fall next month, a restraint reapplied
          after a care conference, a repeat of the same incident — none of them
          could be audited, and the screen offered no explanation, just no button.

          The server was always ready for this. 028's unique index covers OPEN
          reviews only (WHERE completed_at IS NULL), so a new cycle inserts
          cleanly once the previous one is signed, and the signed one stays on the
          record where a surveyor can still be shown it. */}
      {(!review || signed) && !sample ? (
        <div style={{ marginTop: 16 }}>
          <button
            onClick={begin}
            disabled={busy}
            style={{
              padding: "9px 15px",
              borderRadius: 9,
              border: "1px solid var(--ed-ink)",
              background: "var(--ed-ink)",
              color: "var(--ed-canvas)",
              fontWeight: 700,
              cursor: busy ? "default" : "pointer",
            }}
          >
            {busy ? "Starting…" : signed ? "Start a new audit" : "Start audit"}
          </button>
          <div style={{ color: "var(--ed-muted)", fontSize: T.base, marginTop: 8, lineHeight: LH.body }}>
            {signed ? (
              <>
                The audit below was signed off and cannot be edited. Starting a new one opens a fresh worksheet
                against today's reading of the record — use it after a new incident, or when something has changed.
                The signed audit stays on this resident's record.
              </>
            ) : (
              <>
                Starting records who audited, when, and which reading of the record they worked from. The rows the
                system could answer are already filled in below; you enter the rest.
              </>
            )}
          </div>
        </div>
      ) : null}

      {/* THE CHART PULL. Costs no PointClickCare call — the notes and the incident list
          are already in Supabase — but it is a model call, so it is a button a person
          presses rather than something that happens on load.

          The event is PICKED, not guessed. A picked event is a fact; an inferred one is
          a guess, and an audit filed against the wrong event is worse than one filed
          slowly. The model is told which it is either way. */}
      {review && !signed && !sample ? (
        <div
          style={{
            marginTop: 14,
            padding: "12px 14px",
            border: "1px solid var(--ed-violet-line)",
            background: "var(--ed-violet-soft)",
            borderRadius: 10,
          }}
        >
          <div style={{ fontWeight: 800, color: "#4c1d95", fontSize: T.lg }}>Read the chart for me</div>
          <div style={{ color: "#5b21b6", fontSize: T.base, marginTop: 3, lineHeight: LH.body }}>
            Reads this resident's progress notes against all eleven rows and comes back with the chart's own words,
            plus the rows nothing supports. <strong>It proposes; you decide.</strong> Nothing it finds becomes an
            answer until you accept it, and accepting records the answer as yours with the quote attached.
          </div>

          <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            {/* ── THE CONTROL HAS TO FIT THE SCREEN IT IS ON ──────────────────────
                This was one flex ROW: the words "Event this audit follows" and the
                select, side by side. A flex item's default minimum is its content, but
                a bare text node has no such floor — so on a phone the select held its
                380px and the label was squeezed to the width of its longest WORD,
                stacking four lines deep beside a control it is supposed to name.

                A column instead. The label sits above the select at every width, which
                is what a form field looks like anyway, and the select is `width: 100%`
                with `maxWidth: 380` so it fills a narrow screen and stays readable on a
                wide one. `minWidth: 0` because a select's intrinsic width is its widest
                OPTION — here a whole incident line — and without it the control refuses
                to shrink and pushes the card off the screen instead. */}
            <label style={{ fontSize: T.sm, color: "#5b21b6", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, flex: "1 1 240px", minWidth: 0 }}>
              Event this audit follows
              <select
                value={incidentId}
                onChange={(e) => setIncidentId(e.target.value)}
                disabled={busy || !incidents?.length}
                style={{ padding: "7px 8px", borderRadius: 7, border: "1px solid #c4b5fd", fontSize: T.base, width: "100%", maxWidth: 380, minWidth: 0, fontFamily: "inherit" }}
              >
                <option value="">
                  {incidents === null
                    ? "loading…"
                    : incidents.length
                      ? "let the tool work it out (it will say so)"
                      : "no incidents on file for this resident"}
                </option>
                {(incidents || []).map((i) => (
                  <option key={i.incident_id} value={i.incident_id}>
                    {incidentLabel(i)}
                  </option>
                ))}
              </select>
            </label>

            {/* ── THE FILTER, AND WHAT IT IS HONEST ABOUT ────────────────────────
                Only offered once a pull has an event date to measure against. Off by
                default: some rows WANT pre-event evidence — "other methods tried PRIOR
                TO use of the restraint" is one — so defaulting it on would make those
                rows unanswerable while looking tidier.

                It says how many it hid. A filter that silently removes evidence from a
                compliance worksheet is the same failure as a lookup against an empty
                table: the screen looks clean and knows less than it did. */}
            {eventDate ? (
              <label style={{ fontSize: T.sm, color: "#5b21b6", display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={afterEventOnly}
                  onChange={(e) => setAfterEventOnly(e.target.checked)}
                />
                Only evidence written on or after {String(eventDate).slice(0, 10)}
                {afterEventOnly && hiddenByFilter ? (
                  <span style={{ fontWeight: 700 }}>
                    {` — ${hiddenByFilter} hidden`}
                  </span>
                ) : null}
              </label>
            ) : null}
            {/* NO EVENT, NO POSITIONING — said rather than left as an absent control. */}
            {pullRecord && !eventDate ? (
              <span style={{ fontSize: T.xs, color: "#78350f" }}>
                No event date on this pull, so evidence cannot be placed before or after it.
                Pick the event above and read the chart again.
              </span>
            ) : null}
            <button
              type="button"
              onClick={pull}
              disabled={busy}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                fontWeight: 700,
                fontSize: T.base,
                border: "1px solid #4c1d95",
                background: busy ? "#ede9fe" : "#4c1d95",
                color: busy ? "var(--ed-violet-deep)" : "var(--ed-canvas)",
                cursor: busy ? "default" : "pointer",
              }}
            >
              {busy ? "Reading the chart…" : "Pull from the chart"}
            </button>
          </div>

          {incidentId ? null : (
            <div style={{ fontSize: T.xs, color: "var(--ed-violet-deep)", marginTop: 7 }}>
              With no event picked, the tool will say which one it thinks this audit follows and treat that as a
              guess. Picking one is better.
            </div>
          )}
          {pullRun?.incidentRead ? (
            <div style={{ fontSize: T.sm, color: "#4c1d95", marginTop: 7 }}>
              <strong>Read as:</strong> {pullRun.incidentRead}
            </div>
          ) : null}
          {/* A QUOTE THAT IS NOT IN THE CHART IS ITS OWN CATEGORY, AND THE SERIOUS ONE.
              The notice below covers proposals that arrived malformed — no state, no
              reason — which is a tidiness problem. This one means the model produced
              text it presented as the chart's own words and the server could not find
              that text in the notes it had read. Those proposals survive as gaps with
              the quote removed, so the row now asks the nurse to go and look; she is
              entitled to know that is WHY, rather than seeing a row that looks as
              though the chart had nothing on it. */}
          {pullRun?.quotesUnverified ? (
            <div
              style={{
                fontSize: T.sm,
                color: "#7f1d1d",
                background: "var(--ed-alert-soft)",
                border: "1px solid var(--ed-alert-line)",
                borderRadius: 8,
                padding: "7px 10px",
                marginTop: 7,
              }}
            >
              <strong>
                {pullRun.quotesUnverified} proposal(s) quoted text that is not in the notes that were read.
              </strong>{" "}
              The quote was discarded and the row is shown as a gap instead, with the reasoning kept. Nothing was
              answered from it — check those rows in the chart yourself. The quote is never shown, because text the
              record does not contain must not appear on a worksheet in quotation marks.
            </div>
          ) : null}
          {pullRun?.dropped?.length ? (
            <div style={{ fontSize: T.xs, color: "var(--ed-amber-deep)", marginTop: 6 }}>
              {pullRun.dropped.length} proposal(s) were discarded or demoted before storing — they arrived without a
              quote, a state or a reason, named a row this worksheet does not have, or cited text that could not be
              found in the chart. Said out loud rather than dropped quietly, because a thin result and a filtered one
              look the same.
            </div>
          ) : null}
        </div>
      ) : null}

      {openGaps ? (
        <div
          style={{
            marginTop: 12,
            padding: "9px 12px",
            border: "1px solid var(--ed-amber-line)",
            background: "var(--ed-amber-soft)",
            borderRadius: 9,
            fontSize: T.base,
            color: "#78350f",
          }}
        >
          <span className="pia-gap-mark" style={{ display: "inline-block", width: 18, height: 18, lineHeight: "18px", textAlign: "center", borderRadius: 9, background: "var(--ed-amber)", color: "var(--ed-canvas)", fontWeight: 900, fontSize: T.sm, marginRight: 7 }}>!</span>
          {/* ── THE ORDER LIST CAME OFF THIS LIST, AND THE SENTENCE HAD NOT ──────────
              It read "the evidence may be in the order list, the paper chart or the Kardex,
              none of which this tool can read". The order list IS read now — /medications is
              the same route of entry for a restraint order as for a medication order in
              PointClickCare — so naming it among the things we cannot see was the same class
              of stale claim as the indicator-2 string this was written beside. The paper
              chart and the Kardex are still genuinely unreachable. */}
          <strong>{openGaps} row{openGaps === 1 ? "" : "s"} nothing in the readable record supports.</strong>{" "}
          Each one says what should be there. A gap is not a finding of non-compliance — the evidence may be in the
          paper chart or on the CNA Kardex, neither of which this tool can read.
        </div>
      ) : null}

      {/* THE CAVEAT THAT QUALIFIES EVERY ROW, ONCE.
          It used to be the tail of all eleven rows' reasoning, which is how a caveat
          becomes wallpaper — and on the rows whose own source was the order list or
          the Kardex it read as circular. It belongs to the SHEET, so it sits above
          the sheet. */}
      {d.compiled?.worksheetNote ? (
        <div
          style={{
            marginTop: 12,
            padding: "9px 12px",
            border: "1px solid #dbeafe",
            background: "#f0f7ff",
            borderRadius: 9,
            fontSize: T.base,
            color: "#1e3a8a",
            lineHeight: LH.body,
          }}
        >
          {d.compiled.worksheetNote}
        </div>
      ) : null}

      <div style={{ marginTop: 14 }}>
        {/* The five marks and how many rows are sitting in each. This is the check-off
            at sheet level: a DON opening this wants "how many are still mine" before any
            percentage, and "Not read" is that number. It stays visible when the rows
            stack — on a tablet it is the only summary of the sheet there is room for. */}
        <div style={{ padding: "0 0 9px" }}>
          <MarkLegend marks={liveCoverage?.marks || null} />
        </div>
        {/* Column names, so the five marks and the two answer columns are not a
            puzzle. Hidden once the rows stack — see the stylesheet. */}
        <div
          className="pia-colhead"
          style={{
            display: "grid",
            gridTemplateColumns: PIA_GRID_COLUMNS,
            gap: "0 12px",
            alignItems: "end",
            padding: "0 0 5px",
            fontSize: T.micro,
            fontWeight: 800,
            letterSpacing: ".05em",
            textTransform: "uppercase",
            color: "var(--ed-muted-2)",
          }}
        >
          <div />
          <div>Indicator &amp; where it is answered from</div>
          <div style={{ textAlign: "center" }} title="What the record shows. Click a chip to read why.">Rec</div>
          <div>Your answer</div>
          <div>What you found</div>
          <div
            style={{ textAlign: "center" }}
            title="What the row reads as — your answer where you gave one, otherwise the record's. Click a mark for why."
          >
            Reads as
          </div>
        </div>
        {indicators.map((ind) => (
          <IndicatorRow
            key={ind.n}
            indicator={ind}
            row={rowsByN.get(ind.n)}
            draft={drafts[ind.n]}
            // `|| !review` is right for live -- there is nothing to attach a comment to
            // until the audit is started. In the sample there is never a review and never
            // will be, so that clause would have left the reviewer and comments fields
            // frozen while every row above them had become typeable: half an editable
            // worksheet, which reads as a bug rather than as a boundary.
            readOnly={readOnly || (!review && !sample)}
            suggestions={suggestionsByN.get(ind.n) || []}
            pullExamined={pullRecord ? examined.has(Number(ind.n)) : null}
            pullReach={pullReach}
            pullTruncated={!!pullRecord?.notesTruncated}
            eventDate={eventDate}
            busy={busy}
            onAccept={(sg) => decide(sg, "accept")}
            onDismiss={(sg) => decide(sg, "dismiss")}
            onDraft={(patch) => setDrafts((p) => ({ ...p, [ind.n]: { ...p[ind.n], ...patch } }))}
          />
        ))}
      </div>

      {/* ── WHAT THESE ROWS WOULD SAY THE DAY A SCOPE IS GRANTED ────────────────
          Four things the clinical review asked for are unreadable today, not because
          anything is unbuilt but because PointClickCare has not granted the scope: care
          plans, vitals, labs and the administration record.

          Shown so the product can be demonstrated whole, and shown SEPARATELY from the
          grid above for the one reason that matters: a mock value rendered the way a
          compiled one is rendered is a promise the integration cannot keep. The cells
          above keep saying only what the real compiler can establish today. This block
          says what the grant would buy, and says it as a sentence rather than as a cell. */}
      {sample?.preview ? <PreviewBlock preview={sample.preview} /> : null}

      {/* WHERE SAVE WOULD BE, THE FOOT OF THE SHEET SAYS WHY IT IS NOT.
          Reported as "we need a save button". There is one -- Save progress and Sign off,
          below -- but it is wrapped in `review && !signed && !sample`, so until an audit
          is STARTED the bottom of the worksheet held nothing at all.

          The Start audit button is above the grid, which is the right place to begin and
          the wrong place to be told. A nurse works down twelve rows and looks for Save at
          the end; finding nothing, the reasonable conclusion is that the screen has no
          way to keep her answers. She is wrong, and the screen gave her no reason to
          think otherwise.

          So every state that is NOT "open and editable" says so here, in the place the
          absence is noticed, and the two that can be acted on carry the action. The rows
          were already readOnly in each of them -- this adds the sentence, not the rule. */}
      {!(review && !signed) || sample ? (
        <div style={{ marginTop: 18, borderTop: "2px solid var(--ed-line)", paddingTop: 14 }}>
          {sample ? (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ fontSize: T.base, color: "#5b21b6", lineHeight: LH.body, flex: "1 1 320px" }}>
                {sampleEditable ? (
                  <>
                    <strong>Answers here are kept on this device only.</strong> No facility, no server, and not
                    carried to another browser — the residents are invented, so there is nothing to file them
                    against. On a real resident, this is where <em>Sign off</em> appears too; a sample cannot be
                    signed, because a signature has to be somebody&rsquo;s.
                    {sampleSavedAt ? (
                      <> Last kept {new Date(sampleSavedAt).toLocaleString()}.</>
                    ) : null}
                  </>
                ) : (
                  <>
                    <strong>This worked example is signed off, so it is locked.</strong> That is the real behaviour,
                    not a limit of the demo — open one of the unsigned examples to answer rows yourself.
                  </>
                )}
              </div>
              {sampleEditable ? (
                <>
                  <button
                    onClick={() => persist({ complete: false })}
                    style={{
                      padding: "9px 16px", borderRadius: 8, border: "1px solid var(--ed-violet)",
                      background: "var(--ed-violet)", color: "var(--ed-canvas)", fontSize: T.base, fontWeight: 650, cursor: "pointer",
                    }}
                  >
                    Keep my answers
                  </button>
                  <button
                    onClick={() => {
                      clearSampleDraft(aspect, sample.residentId);
                      setDrafts({});
                      setReviewer(sample.review?.reviewer || "");
                      setComments(sample.review?.comments || "");
                      setSampleSavedAt(null);
                      setMsg({ kind: "ok", text: "Sample answers cleared on this device. The record's own cells are unchanged." });
                    }}
                    style={{
                      padding: "9px 16px", borderRadius: 8, border: "1px solid var(--ed-line-hover)",
                      background: "var(--ed-canvas)", color: "var(--ed-ink-2)", fontSize: T.base, cursor: "pointer",
                    }}
                  >
                    Clear mine
                  </button>
                </>
              ) : null}
            </div>
          ) : signed ? (
            <div style={{ fontSize: T.base, color: "#14532d", lineHeight: LH.body }}>
              <strong>
                Signed off by {review?.reviewer || "—"}
                {review?.reviewedOn ? ` on ${review.reviewedOn}` : ""}.
              </strong>{" "}
              This worksheet is the record of that cycle and cannot be edited — not by anyone, and not by the
              nightly scan. To audit this resident again, use <em>Start a new audit</em> above; the signed one
              stays on the record.
            </div>
          ) : (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ fontSize: T.base, color: "#78350f", lineHeight: LH.body, flex: "1 1 320px" }}>
                <strong>Nothing is saved yet — this audit has not been started.</strong> The rows above are the
                record&rsquo;s own answers, shown read-only. Start the audit to enter your own;{" "}
                <em>Save progress</em> and <em>Sign off</em> then appear here.
              </div>
              {/* The same action as the button above the grid, offered again at the end.
                  Pressing it twice is safe -- startReview resumes rather than forking. */}
              <button
                onClick={begin}
                disabled={busy}
                style={{
                  padding: "9px 15px", borderRadius: 9, border: "1px solid var(--ed-ink)",
                  background: busy ? "var(--ed-line)" : "var(--ed-ink)", color: busy ? "var(--ed-muted)" : "var(--ed-canvas)",
                  fontWeight: 700, cursor: busy ? "default" : "pointer", whiteSpace: "nowrap",
                }}
              >
                {busy ? "Starting…" : "Start audit"}
              </button>
            </div>
          )}
        </div>
      ) : null}

      {review && !signed && !sample ? (
        <div style={{ marginTop: 18, borderTop: "2px solid var(--ed-line)", paddingTop: 14 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: T.sm, color: "var(--ed-ink-3)", display: "flex", flexDirection: "column", gap: 4 }}>
              Reviewer
              <input
                value={reviewer}
                onChange={(e) => setReviewer(e.target.value)}
                placeholder="Name and credential"
                style={{ padding: "6px 9px", borderRadius: 7, border: "1px solid var(--ed-line)", fontSize: T.md, minWidth: 210, fontFamily: "inherit" }}
              />
            </label>
            <label style={{ fontSize: T.sm, color: "var(--ed-ink-3)", display: "flex", flexDirection: "column", gap: 4 }}>
              Date reviewed
              <input
                type="date"
                value={reviewedOn}
                onChange={(e) => setReviewedOn(e.target.value)}
                style={{ padding: "6px 9px", borderRadius: 7, border: "1px solid var(--ed-line)", fontSize: T.md, fontFamily: "inherit" }}
              />
            </label>
          </div>
          <label style={{ fontSize: T.sm, color: "var(--ed-ink-3)", display: "flex", flexDirection: "column", gap: 4, marginTop: 10 }}>
            Comments
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={2}
              style={{ padding: "7px 9px", borderRadius: 7, border: "1px solid var(--ed-line)", fontSize: T.md, resize: "vertical", fontFamily: "inherit" }}
            />
          </label>
          <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={() => persist()} disabled={busy || !dirty} style={{ padding: "8px 14px", borderRadius: 8, fontWeight: 700 }}>
              {busy ? "Saving…" : "Save progress"}
            </button>
            <button
              onClick={() => persist({ complete: true })}
              disabled={busy || !reviewer || !reviewedOn}
              title={!reviewer || !reviewedOn ? "A signed audit needs a reviewer name and a date" : "Sign off — this cannot be edited afterwards"}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                fontWeight: 700,
                border: "1px solid #15803d",
                background: !reviewer || !reviewedOn ? "var(--ed-line-2)" : "#15803d",
                color: !reviewer || !reviewedOn ? "var(--ed-muted-2)" : "var(--ed-canvas)",
              }}
            >
              Sign off
            </button>
            <span style={{ color: "var(--ed-muted)", fontSize: T.sm }}>
              Signing is final — the next audit of this resident starts a new worksheet and this one stays as the record
              of this cycle.
            </span>
          </div>
        </div>
      ) : null}

      {msg ? (
        <div
          style={{
            marginTop: 12,
            padding: "9px 11px",
            borderRadius: 8,
            fontSize: T.base,
            background: msg.kind === "err" ? "var(--ed-alert-soft)" : msg.kind === "locked" ? "var(--ed-brand-soft)" : msg.kind === "warn" ? "var(--ed-amber-soft)" : "var(--ed-success-soft)",
            border: `1px solid ${msg.kind === "err" ? "#fca5a5" : msg.kind === "locked" ? "#bfdbfe" : msg.kind === "warn" ? "#fde68a" : "#86efac"}`,
            color: msg.kind === "err" ? "#7f1d1d" : msg.kind === "locked" ? "var(--ed-info-deep)" : msg.kind === "warn" ? "#78350f" : "#14532d",
          }}
        >
          {msg.text}
        </div>
      ) : null}

      {d.history?.length ? (
        <div style={{ marginTop: 16, fontSize: T.sm, color: "var(--ed-muted)" }}>
          Earlier cycles:{" "}
          {d.history.map((h, i) => (
            <span key={h.id}>
              {i ? " · " : ""}
              {h.reviewedOn} ({h.reviewer})
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
